import React, { Component, Fragment } from 'react';
import { Pagination } from '~/Api/Pagination';
import { Product } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { Item as ProductGroupItem } from '~/Api/User/ProductGroup/Product';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { PricedProduct } from '~/Product';
import ProductDetailModal from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ProductsTable from '~/ProductsTable';
import { Item as ProductItem } from '~/Api/Product';
import ActivityIndicator from '~/UI/ActivityIndicator';
import SimplePaginator from '~/UI/SimplePaginator/SimplePaginator';

import PackageSizeInput from './PackageSizeInput';
import ProductProvider from './ProductProvider';

interface Props {
	header: React.ReactNode;
	currency: string | null;
	priceType: string | null;
	filterCalculatedPrice: boolean;
	group: UserProductGroup;
	products: ProductGroupItem[];
	pricedProducts: PricedProduct<ProductGroupItem>[];
	hasNextPage: boolean;
	pagination: Pagination | null;
	isFetchingProducts: boolean;
	isFetchingProductPrices: boolean;
	detailProductId: string | null;
	detailProductTabName: string | null;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onFetch(a?: boolean): void;
	onShowDetail(a: string, b: string): void;
	onCloseDetail(): void;
	onCurrencyChanged(a: string | null, b: PricedProduct<ProductGroupItem>[]): void;
	onPriceChanged(a: string | null, b: string | null, filtered: boolean): void;
	onChangeProductPackageSizeForSingleUnit(groupId: string, productId: string): void;
	onCommitProductPackageSizeForSingleUnit(
		groupId: string,
		productId: string,
		packageSizeForSingleUnit: string | null,
		currency: string | null
	): void;
	onPrune(): void;
	onDeleteItems(group: UserProductGroup, products: Product[], onSuccess: () => void): Promise<void>;
}

interface State {
	removeProduct: Product | null;
	lastSeenProductIdDetail: string | null;
	checkedRows: Product[];
}

export default class ProductList extends Component<Props, State> {
	static defaultProps = {
		currency: null,
		priceType: null,
		detailProductId: null,
		detailProductTabName: null,
	};

	state: State = {
		removeProduct: null,
		lastSeenProductIdDetail: null,
		checkedRows: [],
	};

	componentDidMount() {
		this.props.onFetch();
	}

	componentDidUpdate(prevProps: Props) {
		const { group, currency, pricedProducts, onFetch, onCurrencyChanged } = this.props;

		if (prevProps.group.id !== group.id || prevProps.group.exchangeRates !== group.exchangeRates) {
			onFetch(true);
		} else if (prevProps.currency !== currency && pricedProducts.length > 0) {
			onCurrencyChanged(currency, pricedProducts);
		}
	}

	componentWillUnmount() {
		this.props.onPrune();
	}

	private readonly checkRow = (product: Product, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: Product[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	renderPackageCell = (product: PricedProduct<ProductItem>, index: number): React.ReactNode => {
		const {
			products,
			group,
			currency,
			onChangeProductPackageSizeForSingleUnit,
			onCommitProductPackageSizeForSingleUnit,
		} = this.props;
		const groupProduct = products[index];

		return groupProduct.product.dataset.source.singleUnitPackageSize ? (
			<PackageSizeInput
				value={groupProduct.packageSizeForSingleUnit}
				onCommit={(value) =>
					onCommitProductPackageSizeForSingleUnit(
						group.id,
						groupProduct.product.id,
						value ? String(value) : null,
						currency
					)
				}
				onChange={() => onChangeProductPackageSizeForSingleUnit(group.id, groupProduct.product.id)}
			/>
		) : null;
	};

	handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	render() {
		const {
			props: {
				header,
				currency,
				priceType,
				filterCalculatedPrice,
				group,
				pricedProducts,
				hasNextPage,
				pagination,
				isFetchingProducts,
				isFetchingProductPrices,
				detailProductId,
				detailProductTabName,
				productDetailLink,
				onFetch,
				onShowDetail,
				onPriceChanged,
			},
			state: { removeProduct, lastSeenProductIdDetail, checkedRows },
		} = this;

		return (
			<Fragment>
				<ProductsTable
					currencies={group.exchangeRates.availableCurrencies}
					currency={currency}
					detailLink={productDetailLink}
					filterCalculatedPrice={filterCalculatedPrice}
					footer={
						<Fragment>
							{isFetchingProducts ? (
								<ActivityIndicator />
							) : hasNextPage ? (
								<SimplePaginator count={pagination?.nextGrowedPage?.perPage} onLoadMore={() => onFetch(false)} />
							) : null}
							{pricedProducts.length === 0 && !isFetchingProducts ? <ProductInstructions /> : null}
						</Fragment>
					}
					footerHeight={65}
					header={header}
					highlightProductId={lastSeenProductIdDetail}
					checkedRows={checkedRows}
					checkRow={this.checkRow}
					initialHeaderHeight={192}
					isFetching={isFetchingProductPrices}
					packageCell={this.renderPackageCell}
					priceHeader={null}
					priceType={priceType}
					products={pricedProducts}
					sortFields={null}
					onPriceChange={onPriceChanged}
				/>

				{detailProductId ? (
					<ProductProvider groupId={group.id} id={detailProductId}>
						<ProductDetailModal
							currency={currency}
							groupId={group.id}
							isFetching={isFetchingProducts}
							isFetchingPrices={isFetchingProductPrices}
							product={null}
							productId={detailProductId}
							products={pricedProducts}
							selectedTabName={detailProductTabName || ''}
							shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
								detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName || '')
							}`}
							onClose={this.handleCloseDetail}
							onCurrencyChange={(currency) => onPriceChanged(currency, priceType, filterCalculatedPrice)}
							onChange={(productId) => onShowDetail(productId, detailProductTabName || '')}
							onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
						/>
					</ProductProvider>
				) : null}

				{removeProduct ? (
					<ProductGroupRemoveProductModal
						group={group}
						product={removeProduct}
						onClose={() => this.setState({ removeProduct: null })}
						onSuccess={() => this.setState({ removeProduct: null })}
					/>
				) : null}
				{checkedRows.length > 0 ? (
					<CopyGroupProductActionPanel
						group={group}
						products={checkedRows}
						selectedProductsCount={this.countRows()}
						onClickCancelDelete={this.onClickCancelDelete}
						onClickRemoveRow={this.onDeleteItems}
						onProductsCopied={this.handleProductsCopied}
					/>
				) : null}
			</Fragment>
		);
	}
}
